import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconBrandADManager } from '@components/icons/brand-ad-manager';
import { IconRemove } from '@components/icons/remove';
import { IntegrationCard } from '@components/integration-card';
import { RelativeSpinner } from '@components/loading-spinner';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { useStore } from 'models/Provider';

import { adManagerGetJob, adManagerGetSettings, adManagerRemoveJob, adManagerSaveJob } from './api';

type IntegrationParams = {
  jobID: string;
  integrationID: string;
};

export const ADManagerJobApp = () => {
  const { t } = useStore();

  return (
    <Stack space={6}>
      <IntegrationCard
        icon={IconBrandADManager}
        header={t('integrations.admanager.title')}
        text={t('integrations.admanager.description')}
      />
      <Content />
    </Stack>
  );
};

const Content = () => {
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const { jobID } = useParams() as IntegrationParams;
  const jobQuery = useQuery({
    queryKey: ['ad-manager-job', jobID],
    queryFn: () => adManagerGetJob(jobID),
  });

  const client = useQueryClient();
  const removeJobMutation = useMutation(() => adManagerRemoveJob(jobID), {
    onSuccess: () => {
      client.invalidateQueries(['ad-manager-job', jobID]);
      client.invalidateQueries(['ad-manager-settings']);
      showSuccess(t('integrations.admanager.job.remove.success'));
    },
    onError: () => {
      showError(t('integrations.admanager.job.remove.error'));
    },
  });

  if (jobQuery.isLoading) {
    return <RelativeSpinner />;
  }

  if (!jobQuery.data?.templateName) {
    return <Form />;
  }

  return (
    <Card space={6}>
      <Stack space={4}>
        <Stack space={0}>
          <Text font="semibold14" text={t('integrations.admanager.job.templates.label')} />
          <Text text={jobQuery.data.templateName} />
        </Stack>
        <Stack space={0} orientation="horizontal" justify="flex-end">
          <Button
            variant="ghost"
            text={t('common.forms.button.remove')}
            iconLeft={<IconRemove />}
            onClick={() => removeJobMutation.mutate()}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

const Form = () => {
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const { jobID } = useParams() as IntegrationParams;
  const settingsQuery = useQuery({
    queryKey: ['ad-manager-settings'],
    queryFn: () => adManagerGetSettings(),
  });

  const [template, setTemplate] = useState('');

  const client = useQueryClient();
  const submit = useMutation(() => adManagerSaveJob(template, jobID), {
    onSuccess: () => {
      client.invalidateQueries(['ad-manager-job', jobID]);
      showSuccess(t('integrations.admanager.job.save.success'));
    },
    onError: () => {
      showError(t('integrations.admanager.job.save.error'));
    },
  });

  if (settingsQuery.isLoading) {
    return <RelativeSpinner />;
  }

  if (!settingsQuery.data?.connected) {
    return (
      <Card space={6}>
        <Text text={t('integrations.admanager.job.notconnected')} />
      </Card>
    );
  }

  return (
    <Card variant="transparent" space={6}>
      <Stack space={4}>
        <Select
          options={(settingsQuery.data?.templates ?? []).map((templateName) => {
            return {
              label: templateName,
              value: templateName,
            };
          })}
          label={t('integrations.admanager.job.templates.label')}
          onChange={setTemplate}
        />
        <Stack space={0} orientation="horizontal" justify="flex-end">
          <Button
            isDisabled={template === ''}
            variant="primary-blue"
            text={t('common.forms.button.save')}
            onClick={() => submit.mutate()}
          />
        </Stack>
      </Stack>
    </Card>
  );
};
